/**
 * IRA 账户在入金的时候上方展示的税年限额信息
 */
import numberal from '@tiger/numeral';
import { useSWR } from '@tiger/swr';
import { FC, useEffect, useMemo } from 'react';
import { $Formutil, connect, EasyField } from 'react-formutil';
import FieldSelect from 'components/FieldSelect';
import FormItem from 'components/FormItem';
import Loading from 'components/Loading';
import { openIRALimitsModal } from 'modules/Deposit/helper';
import { IRALimits } from 'modules/Deposit/type';
import iconTip from 'static/images/icons/icon-tip.svg';
import security from 'stores/security';
import API from 'utils/API';
import classlist from 'utils/classlist';
import { printf } from 'utils/i18n';
import style from './style.scss';

interface TaxLimitProps {
    $formutil: $Formutil;
    /**
     * 成功页面的税年
     */
    successYear?: string;
    /**
     * 提交的金额
     */
    cost?: number;
}

const emptyArr = [];
const emptyFn = () => {};

const TaxLimit: FC<TaxLimitProps> = ({ $formutil, successYear, cost = 0 }) => {
    const taxYear = successYear || $formutil.$params.tax_year;
    // 存在 successYear 时，组件只是作为显示作用，可能并不会放在 Form 中，所以可能会拿不到 $formutil
    const setFormValues = successYear ? emptyFn : $formutil.$setValues;

    const { data = emptyArr, isLoading } = useSWR<IRALimits[]>(
        security.isIRAAccount && {
            url: API.TRADE.deposit.getIRALimits(),
            params: {
                accountId: security.currentAccountId
            }
        },
        {
            dataChain: resp => resp.data
        }
    );

    const { limitAmount, progress } = useMemo(() => {
        const limitMap: Record<string, IRALimits> = {};

        data.forEach(item => {
            limitMap[item.taxYear] = item;
        });

        if (!taxYear || !limitMap[taxYear] || data.length === 0) {
            return {
                limitAmount: 0,
                progress: '0%'
            };
        }

        const { totalLimit, remainingLimit } = limitMap[taxYear];

        return {
            limitAmount: Math.max(0, remainingLimit - cost),
            progress: numberal((totalLimit - Math.max(0, remainingLimit - cost)) / totalLimit).format('00.00%')
        };
    }, [data, taxYear, cost]);

    useEffect(() => {
        // 如果存在 successYear，说明当前组件用于结果页，不需要设置默认值
        if (successYear) {
            return;
        }

        if (typeof limitAmount === 'number') {
            setFormValues({
                tax_limit: limitAmount
            });
        }
    }, [limitAmount, successYear, setFormValues]);

    useEffect(() => {
        if (taxYear || data.length === 0) {
            return;
        }

        setFormValues({
            tax_year: data[0].taxYear
        });
    }, [data, taxYear, setFormValues]);

    if (!security.isIRAAccount || (!successYear && data.length === 0)) {
        return null;
    }

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div className={classlist('tax-limit', style.root, { [style.successMode]: !!successYear })}>
            <div className={style.limit}>
                <div className={style.titleWrapper}>
                    <span className={style.limitTitle}>
                        {__('Remaining limit of')} ${numberal(limitAmount).format('0,0.[00]')}
                    </span>
                    {successYear && (
                        <>
                            <span className={style.subTitle}>
                                {successYear} {__('Tax Year')}
                            </span>
                            <img src={iconTip} alt="tip" onClick={() => openIRALimitsModal()} />
                        </>
                    )}
                </div>
                <div className={style.progress}>
                    <div className={style.used} style={{ width: progress }}></div>
                </div>
            </div>
            {!successYear && (
                <FormItem
                    required
                    name="tax_year"
                    $defaultValue={successYear || String(new Date().getFullYear())}
                    label={
                        <span className={style.yearTitle}>
                            <span>{__('Tax Year')}</span>
                            <img src={iconTip} alt="tip" onClick={() => openIRALimitsModal()} />
                        </span>
                    }>
                    <FieldSelect
                        disabled={data.length <= 1}
                        data={data.map(item => ({
                            value: item.taxYear,
                            label: printf(__('%s Year'), item.taxYear)
                        }))}
                    />
                </FormItem>
            )}
            <EasyField type="hidden" key="tax_limit" name="tax_limit" />
        </div>
    );
};

export default connect(TaxLimit);
